import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getDocument, GlobalWorkerOptions, version as pdfjsVersion } from 'pdfjs-dist';
import 'pdfjs-dist/web/pdf_viewer.css';

// Use locally copied worker (placed in public/pdf.worker.mjs). Fallback to CDN.
if (!GlobalWorkerOptions.workerSrc) {
  GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';
}
// Provide a lightweight runtime check; if fetch fails, swap to CDN.
fetch('/pdf.worker.mjs', { method: 'HEAD' }).catch(() => {
  console.warn('Local pdf.worker.mjs not accessible, falling back to CDN');
  GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsVersion}/pdf.worker.min.js`;
});

const PdfEditor = () => {
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fields, setFields] = useState([]);
  const [pageCanvases, setPageCanvases] = useState([]);
  const [jsonOutput, setJsonOutput] = useState(null);
  const [pdfFileName, setPdfFileName] = useState('No PDF loaded');

  const scale = 1.5; // Fixed scale for better readability

  const pdfRectToViewportRect = (rect, viewport) => {
    const [llx, lly, urx, ury] = rect;
    const x = llx * viewport.scale;
    const y = (viewport.height - ury * viewport.scale);
    const width = (urx - llx) * viewport.scale;
    const height = (ury - lly) * viewport.scale;
    return { x, y, width, height };
  };

  const loadPdf = useCallback(async (file) => {
    if (!file) return;

    // Clear previous PDF state first
    setPageCanvases([]);
    setFields([]);
    setJsonOutput(null);
    setError(null);
    setLoading(true);

    try {
      const data = await file.arrayBuffer();
      setPdfFileName(file.name);

      const pdf = await getDocument({ data }).promise;
      const tempPages = [];
      const tempFields = [];

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale });

        // Render page to canvas
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport }).promise;
        tempPages.push({ pageNum, canvas, width: viewport.width, height: viewport.height });

        // Extract annotations (widgets for form fields)
        const annotations = await page.getAnnotations();
        annotations.filter(a => a.subtype === 'Widget').forEach(a => {
          const rect = pdfRectToViewportRect(a.rect, viewport);
          let type = a.fieldType || 'text';

          if (type === 'Btn') {
            if (a.checkBox) type = 'checkbox';
            else if (a.radioButton) type = 'radio';
            else type = 'button';
          }

          let value = a.fieldValue || '';
          if ((type === 'date' || /date/i.test(a.fieldName || '')) && !value) {
            value = new Date().toISOString().slice(0, 10);
          }

          tempFields.push({
            name: a.fieldName || a.fullName || `field_${tempFields.length}`,
            page: pageNum,
            rect,
            value,
            type,
            checked: type === 'checkbox' ? (a.fieldValue === 'Yes' || a.fieldValue === true) : undefined
          });
        });
      }

      setPageCanvases(tempPages);
      setFields(tempFields);
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      loadPdf(file);
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      setError('Please select a valid PDF file');
    }
  };

  const handleChange = (name, value) => {
    setFields(prev => prev.map(f => {
      if (f.name !== name) return f;
      if (f.type === 'checkbox') {
        return { ...f, value, checked: value === 'Yes' };
      }
      return { ...f, value };
    }));
  };

  const extractJson = () => {
    const out = fields.reduce((acc, f) => {
      acc[f.name] = f.value;
      return acc;
    }, {});
    setJsonOutput(JSON.stringify(out, null, 2));
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h2 style={styles.title}>PDF Field Mapping Guide</h2>
        <div style={styles.headerActions}>
          <button
            style={styles.primaryButton}
            onClick={() => fileInputRef.current?.click()}
          >
            Upload PDF
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <button
            style={styles.secondaryButton}
            onClick={extractJson}
            disabled={fields.length === 0}
          >
            Extract JSON
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={styles.mainContent}>
        {/* Left Side - PDF Preview (60%) */}
        <div style={styles.leftPanel}>
          <div style={styles.panelHeader}>
            <h3 style={styles.panelTitle}>PDF Preview</h3>
            <span style={styles.fileName}>{pdfFileName}</span>
          </div>

          {loading && (
            <div style={styles.centerMessage}>
              <div style={styles.loader}></div>
              <p>Loading PDF...</p>
            </div>
          )}

          {error && (
            <div style={styles.centerMessage}>
              <p style={styles.errorText}>Error: {error}</p>
            </div>
          )}

          {!loading && !error && pageCanvases.length === 0 && (
            <div style={styles.centerMessage}>
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#7fb3a3" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <line x1="9" y1="15" x2="15" y2="15" />
              </svg>
              <p style={styles.emptyText}>Click "Upload PDF" to get started</p>
            </div>
          )}

          <div ref={containerRef} style={styles.pdfContainer}>
            {pageCanvases.map(p => (
              <div key={p.pageNum} style={styles.pageWrapper}>
                <div style={styles.pageNumber}>Page {p.pageNum}</div>
                {p.canvas && (
                  <div
                    style={styles.canvasWrapper}
                    ref={el => {
                      if (el && !el.firstChild) el.appendChild(p.canvas);
                    }}
                  />
                )}
                {fields.filter(f => f.page === p.pageNum).map(f => {
                  if (f.type === 'checkbox') {
                    return (
                      <div
                        key={f.name}
                        style={{
                          position: 'absolute',
                          left: f.rect.x,
                          top: f.rect.y,
                          height: f.rect.height,
                          display: 'flex',
                          alignItems: 'center',
                          pointerEvents: 'auto',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!f.checked}
                          onChange={e => handleChange(f.name, e.target.checked ? 'Yes' : 'Off')}
                          style={{
                            width: Math.min(f.rect.width, f.rect.height),
                            height: Math.min(f.rect.width, f.rect.height),
                            accentColor: '#7fb3a3',
                            margin: 0,
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>
                    );
                  }

                  if (f.name.trim().toLowerCase() === 'date') {
                    const today = new Date().toISOString().slice(0, 10);
                    return (
                      <input
                        key={f.name}
                        type="date"
                        value={f.value || today}
                        onChange={e => handleChange(f.name, e.target.value)}
                        style={{
                          position: 'absolute',
                          left: f.rect.x,
                          top: f.rect.y,
                          width: f.rect.width,
                          height: f.rect.height,
                          border: '1px solid #ddd',
                          fontSize: Math.min(f.rect.height * 0.6, 16),
                          padding: 2,
                          boxSizing: 'border-box',
                          background: 'rgba(255,255,255,0.8)',
                        }}
                      />
                    );
                  }

                  return (
                    <input
                      key={f.name}
                      value={f.value}
                      onChange={e => handleChange(f.name, e.target.value)}
                      style={{
                        position: 'absolute',
                        left: f.rect.x,
                        top: f.rect.y,
                        width: f.rect.width,
                        height: f.rect.height,
                        background: 'rgba(255,255,255,0.8)',
                        border: '1px solid #ddd',
                        fontSize: Math.min(f.rect.height * 0.6, 16),
                        padding: 2,
                        boxSizing: 'border-box',
                      }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Right Side - JSON Output (40%) */}
        <div style={styles.rightPanel}>
          <div style={styles.panelHeader}>
            <h3 style={styles.panelTitle}>Extracted JSON</h3>
            {jsonOutput && (
              <button
                style={styles.copyButton}
                onClick={() => {
                  navigator.clipboard.writeText(jsonOutput);
                  alert('JSON copied to clipboard!');
                }}
              >
                Copy
              </button>
            )}
          </div>

          {!jsonOutput ? (
            <div style={styles.centerMessage}>
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2">
                <polyline points="16 18 22 12 16 6" />
                <polyline points="8 6 2 12 8 18" />
              </svg>
              <p style={styles.emptyText}>Click "Extract JSON" to see the data</p>
            </div>
          ) : (
            <pre style={styles.jsonOutput}>{jsonOutput}</pre>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    height: '100vh',
    width: '100vw',
    display: 'flex',
    flexDirection: 'column',
    background: '#f5f5f5',
    overflow: 'hidden',
  },
  header: {
    background: 'white',
    padding: '20px 30px',
    borderBottom: '2px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    color: '#333',
    fontWeight: '600',
  },
  headerActions: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  primaryButton: {
    background: '#7fb3a3',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background 0.2s',
  },
  secondaryButton: {
    background: '#6b7280',
    color: 'white',
    border: 'none',
    padding: '10px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background 0.2s',
  },
  mainContent: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
  },
  leftPanel: {
    width: '60%',
    background: '#fafafa',
    borderRight: '2px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    height: '100%',
  },
  rightPanel: {
    width: '40%',
    background: 'white',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    height: '100%',
  },
  panelHeader: {
    padding: '15px 20px',
    borderBottom: '1px solid #e0e0e0',
    background: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  panelTitle: {
    margin: 0,
    fontSize: '16px',
    color: '#555',
    fontWeight: '600',
  },
  fileName: {
    fontSize: '13px',
    color: '#999',
    fontStyle: 'italic',
  },
  pdfContainer: {
    flex: 1,
    overflow: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    minHeight: 0,
  },
  pageWrapper: {
    position: 'relative',
    background: 'white',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    borderRadius: '4px',
    overflow: 'visible',
    flexShrink: 0,
  },
  pageNumber: {
    position: 'absolute',
    top: '-25px',
    left: '0',
    fontSize: '12px',
    color: '#666',
    fontWeight: '500',
  },
  canvasWrapper: {
    display: 'block',
  },
  centerMessage: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#999',
    gap: '15px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#999',
    margin: 0,
  },
  errorText: {
    color: '#e74c3c',
    fontSize: '14px',
  },
  loader: {
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #7fb3a3',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite',
  },
  jsonOutput: {
    flex: 1,
    margin: 0,
    padding: '20px',
    background: '#1e1e1e',
    color: '#d4d4d4',
    fontSize: '13px',
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
    overflow: 'auto',
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
  },
  copyButton: {
    background: '#7fb3a3',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
  },
};

// Add CSS animation for loader
const styleSheet = document.styleSheets[0];
const keyframes = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;
styleSheet.insertRule(keyframes, styleSheet.cssRules.length);

export default PdfEditor;
